<template lang="pug">
  .journal-entries#journal-top
    .level.is-mobile.is-custom-mobile
      .level-left
        WtTitle(card-title-large) {{ title }}
      .level-right
        .level-item
          BSwitch(
            v-model="photosOnly"
            :native-value="true"
            size="is-large"
          ) {{ photosOnlyText }}
        .level-item
          BCheckboxButton(
            v-if="isNewsfeed"
            v-model="friends"
            size="is-small"
            :native-value="true"
          ) {{ friendsText }}
    .level.is-marginless(v-if="!_.isEmpty(journalEntryStats) && !isNewsfeed")
      .level-left
          template(v-for="(entry, index) in entryCounts")
            BTag.journal-tag(
              v-if="entry.value > 0"
              :key="`${index}-${entry.id}`"
            )
              | {{ entry.value }} {{ entry.title }}

    span(
      v-if="!_.isEmpty(journalData)"
      v-for="item in journalData"
      :key="item.id"
    )
      .media
      WtMediaObject(
        type="journal"
        :data="item"
        :store-comment-action="storeNewJournalComment"
        :edit-comment-action="editJournalComment"
        :delete-comment-action="deleteJournalComment"
        :page-info="{ photosOnly, friends, page }"
        :is-newsfeed="isNewsfeed"
        :is-profile="isProfile"
      )
    .empty-state(v-if="_.isEmpty(journalData)")
      p Hey there,
      br
      p {{ emptyJournalsMessage }}

    progress( max="100" v-if="isLoading === true").progress.is-small.is-primary
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import WtMediaObject from "@@/WtMediaObject/WtMediaObject";
import WtTitle from "@@/WtTitle/WtTitle";
import WtButton from "@@/WtButton/WtButton";

export default {
  name: "Journal",

  components: {
    WtMediaObject,
    WtTitle,
    WtButton
  },

  props: {
    journalEntryStats: {
      type: Object,
      default: null
    },

    journalData: {
      type: Array,
      required: true
    },

    title: {
      type: String,
      default: "Entries"
    },

    fetchAction: {
      type: Function,
      default: () => {}
    },

    isNewsfeed: {
      type: Boolean,
      default: false
    },

    isLastPage: {
      type: Boolean,
      default: false
    },

    isProfile: {
      type: Boolean,
      default: false
    },

    memberUsername: {
      type: String,
      default: "Username"
    },

    memberHasEntries: {
      type: Boolean,
      default: false
    },

    loaded: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      photosOnly: false,
      friends: false,
      page: 1,
      isLoading: false
    };
  },

  computed: {
    ...mapGetters(["isMobile"]),

    emptyJournalsMessage() {
      return this.isProfile && this.memberHasEntries && this.loaded
        ? `${this.memberUsername}'s journal entries are not public.`
        : "Journal entries will show here when data is available.";
    },

    entryCounts() {
      const publicCount = this.journalEntryStats.publicCount;
      const friendsCount = this.journalEntryStats.friendsOnlyCount;
      const privateCount = this.journalEntryStats.privateCount;
      return [
        {
          id: 1,
          title: `public ${this.pluralizeEntry(publicCount)}`,
          value: publicCount
        },
        {
          id: 2,
          title: `friends-only ${this.pluralizeEntry(friendsCount)}`,
          value: friendsCount
        },
        {
          id: 3,
          title: `private ${this.pluralizeEntry(privateCount)}`,
          value: privateCount
        }
      ];
    },

    photosOnlyText() {
      if (this.photosOnly) {
        return "All posts";
      }
      return `Posts with photos`;
    },

    friendsText() {
      if (this.friends) {
        return "View all posts";
      }
      return `View Friend's posts`;
    }
  },

  watch: {
    photosOnly() {
      return this.fetchItems(null, true);
    },
    friends() {
      return this.fetchItems(null, true);
    }
  },

  mounted() {
    this.getNextPage();
  },

  methods: {
    ...mapActions("members", [
      "storeComment",
      "editComment",
      "deleteComment",
      "updateJournal"
    ]),
    ...mapActions(["snackBar"]),

    fetchItems(page = null, resetList = false) {
      if (this.isLoading) {
        return;
      }
      this.isLoading = true;

      return this.fetchAction(
        this.photosOnly,
        this.friends,
        page,
        resetList
      ).then(() => {
        this.isLoading = false;

        if (this.photosOnly || this.friends) {
          // force the journals back to the top
          this.$scrollTo("#journal-top", 500, { offset: -100 });
        }
      });
    },

    getNextPage() {
      const element = document.getElementById("journal-top");
      window.onscroll = () => {
        // want to send request sequentially to keep the data in sorted manner
        if (this.isLoading) {
          return;
        }
        // if web, fetch journals at bottom of page scroll
        if (!this.isMobile) {
          if (this.isLastPage === true) {
            return;
          }
          if (
            document.documentElement.scrollHeight -
              document.documentElement.scrollTop -
              document.documentElement.clientHeight <
            1
          ) {
            this.page += 1;

            this.fetchItems(this.page, false);

            if (this._.isEmpty(this.journalData)) {
              return;
            }
          }
          // if mobile, fetch journals at bottom of div scroll
        } else {
          if (this.isLastPage === true) {
            return;
          }
          let divBottom =
            element.getBoundingClientRect().bottom -
            document.documentElement.clientHeight;
          if (0 < divBottom < 1) {
            this.isLoading = true;
            this.page += 1;

            this.fetchItems(this.page, false);

            if (this._.isEmpty(this.journalData)) {
              return;
            }
            this.isLoading = false;
          }
        }
      };
    },

    storeNewJournalComment(payload) {
      Object.assign(payload, {
        photosOnly: this.photosOnly,
        friends: this.friends,
        page: this.page,
        memberId: this.$auth.user().id
      });

      return this.storeComment(payload).then(response => {
        if (response && response.data.success) {
          this.page = 1;
          this.fetchItems(this.page, true);
          this.snackBar({
            message: `Thank you for making a comment!`
          });
        }
      });
    },

    editJournalComment(payload) {
      return this.editComment(payload).then(response => {
        if (response && response.data.success) {
          this.page = 1;
          this.fetchItems(this.page, true);
          this.snackBar({
            message: `Your comment has been updated.`
          });
        }
      });
    },

    async deleteJournalComment(payload) {
      this.$buefy.dialog.confirm({
        title: "Delete This Comment",
        confirmText: "Delete This Comment",
        type: "is-danger",
        hasIcon: true,
        message: "Are you sure you want delete this comment?",
        onConfirm: async () => {
          return this.deleteComment(payload).then(response => {
            if (response && response.data.success) {
              this.page = 1;
              this.fetchItems(this.page, true);
              this.snackBar({
                message: `Your comment has been deleted.`
              });
            }
          });
        }
      });
    },

    pluralizeEntry(value) {
      return value > 1 ? "entries" : "entry";
    }
  }
};
</script>

<style lang="sass" scoped>
.journal-entries
  .journal-tag
    margin: 0 5px
    &:first-child
      margin-left: 0
.is-custom-mobile
  @media screen and (max-width: 480px)
    flex-direction: column
    align-items: flex-start
  .level-right
    @media screen and (max-width: 480px)
      margin: 1rem 0 !important
</style>
